/** Photometric (value) and spatial (distance) calibration. */
import { useState } from 'preact/hooks';
import { areaStats } from '../../core/area';
import { buildCalibration, DEFAULT_CALIBRATION, type CalibrationMode, type PhotometricCalibration } from '../../core/calibration';
import { computePlane, makeContext } from '../../core/pipeline';
import { isArea, roiBounds } from '../../core/roi';
import { fmt, SPATIAL_UNITS, type SpatialUnit } from '../../core/units';
import { frames, notify, pipeline, selectedLine, selectedRoi, spatial, tool, updateSignal } from '../../app/store';
import { calibrationRequest } from '../../view/viewState';
import { Button, Field, Hint, NumberField, NumberInput, Section, Select, SelectField, Toggle } from '../controls';
import { Icon } from '../icons';

const UNIT_PRESETS = ['cd/m²', 'W/(sr·m²)', 'lx', 'W/m²', 'nt', 'fL', 'rel.', 'DN'];

function setCal(patch: Partial<PhotometricCalibration>) {
  updateSignal(pipeline, { calibration: { ...pipeline.value.calibration, ...patch } });
}

/** Uncalibrated mean luminance Y of the selected area / point ROI. */
export function measureRoiY(): number | null {
  const r = selectedRoi.value;
  const f = frames.A.value;
  if (!r || !f || !(isArea(r) || r.kind === 'point')) return null;
  const ctx = makeContext(
    f,
    { ...pipeline.value, calibration: { ...DEFAULT_CALIBRATION } },
    { dark: frames.dark.value, flat: frames.flat.value },
  );
  const b = roiBounds(r);
  const plane = computePlane(ctx, 'Y', { x: b.x - 1, y: b.y - 1, w: b.w + 2, h: b.h + 2 });
  const st = areaStats(r, plane, null, null);
  return st.n > 0 ? st.mean : null;
}

function PhotometricSection() {
  const cal = pipeline.value.calibration;
  const fit = buildCalibration(cal);
  const needsPoints = cal.mode === 'poly' || cal.mode === 'lut' || cal.fitFromPoints;
  const addPoint = () => {
    const y = measureRoiY();
    if (y === null) return notify('warn', 'Select a rectangle, ellipse, polygon or point ROI placed on a reference patch first');
    setCal({ points: [...cal.points, { measured: y, reference: NaN, label: selectedRoi.value?.name }] });
  };
  return (
    <Section title="Photometric calibration" help="cal.photometric">
      <SelectField<CalibrationMode>
        label="Model"
        help="cal.mode"
        testId="cal-mode"
        value={cal.mode}
        options={[
          { value: 'none', label: 'None (relative values)' },
          { value: 'scale', label: 'Scale factor  k·Y' },
          { value: 'twoPoint', label: 'Gain + offset  g·Y + o' },
          { value: 'poly', label: 'Polynomial fit' },
          { value: 'lut', label: 'Lookup table (piecewise linear)' },
        ]}
        onChange={(v) => setCal({ mode: v })}
      />
      {cal.mode !== 'none' && (
        <>
          <Field label="Unit" help="cal.unit">
            <span class="inline">
              <input
                type="text"
                class="unit-input"
                value={cal.unit}
                data-testid="cal-unit"
                onChange={(e) => setCal({ unit: (e.target as HTMLInputElement).value })}
              />
              <Select
                value=""
                options={[{ value: '', label: 'Presets' }, ...UNIT_PRESETS.map((u) => ({ value: u, label: u }))]}
                onChange={(v) => v && setCal({ unit: v })}
              />
            </span>
          </Field>
          {(cal.mode === 'scale' || cal.mode === 'twoPoint') && (
            <Toggle
              label="Fit from reference points"
              checked={cal.fitFromPoints}
              help="cal.fit"
              onChange={(v) => setCal({ fitFromPoints: v })}
            />
          )}
          {cal.mode === 'scale' && !cal.fitFromPoints && (
            <NumberField
              label="k"
              help="cal.scale"
              value={cal.scale}
              testId="cal-scale"
              unit={`${cal.unit} per unit`}
              onChange={(v) => setCal({ scale: v })}
            />
          )}
          {cal.mode === 'twoPoint' && !cal.fitFromPoints && (
            <>
              <NumberField label="Gain g" help="cal.gain" value={cal.gain} onChange={(v) => setCal({ gain: v })} />
              <NumberField label="Offset o" help="cal.offset" value={cal.offset} unit={cal.unit} onChange={(v) => setCal({ offset: v })} />
            </>
          )}
          {cal.mode === 'poly' && (
            <NumberField
              label="Degree"
              help="cal.degree"
              value={cal.polyDegree}
              integer
              min={1}
              max={5}
              onChange={(v) => setCal({ polyDegree: v })}
            />
          )}
          {needsPoints && (
            <div class="cal-points" data-help="cal.points">
              <div class="subhead">
                Reference points
                <span class="spacer" />
                <Button small icon="plus" help="cal.addFromRoi" onClick={addPoint} testId="cal-add-roi">
                  From ROI
                </Button>
                <Button
                  small
                  icon="plus"
                  help="cal.addManual"
                  onClick={() => setCal({ points: [...cal.points, { measured: NaN, reference: NaN }] })}
                >
                  Manual
                </Button>
              </div>
              <table class="grid">
                <thead>
                  <tr>
                    <th>Label</th>
                    <th>Measured Y (rel.)</th>
                    <th>Reference [{cal.unit}]</th>
                    <th>Residual</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {cal.points.map((p, i) => {
                    const upd = (patch: Partial<typeof p>) =>
                      setCal({ points: cal.points.map((q, j) => (j === i ? { ...q, ...patch } : q)) });
                    const valid = cal.points
                      .slice(0, i + 1)
                      .filter((q) => Number.isFinite(q.measured) && Number.isFinite(q.reference)).length;
                    const res = Number.isFinite(p.measured) && Number.isFinite(p.reference) ? fit.residuals[valid - 1] : NaN;
                    return (
                      <tr key={i}>
                        <td>
                          <input
                            type="text"
                            class="cell-input"
                            value={p.label ?? ''}
                            onChange={(e) => upd({ label: (e.target as HTMLInputElement).value })}
                          />
                        </td>
                        <td>
                          <NumberInput value={Number.isFinite(p.measured) ? p.measured : null} onChange={(v) => upd({ measured: v })} />
                        </td>
                        <td>
                          <NumberInput
                            value={Number.isFinite(p.reference) ? p.reference : null}
                            placeholder="enter"
                            testId={`cal-ref-${i}`}
                            onChange={(v) => upd({ reference: v })}
                          />
                        </td>
                        <td class="mono">{fmt(res, 4)}</td>
                        <td>
                          <button
                            type="button"
                            class="icon-btn danger"
                            onClick={() => setCal({ points: cal.points.filter((_, j) => j !== i) })}
                            aria-label="Remove point"
                          >
                            <Icon name="trash" size={13} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {fit.error ? (
            <Hint kind="error">{fit.error}</Hint>
          ) : (
            <div class="cal-summary" data-help="cal.summary">
              <div>
                f(Y) = <span class="mono">{fit.description}</span>
              </div>
              {fit.residuals.length > 0 && (
                <div class="muted small">
                  RMSE {fmt(fit.rmse, 4)} {cal.unit} · R² {fmt(fit.r2, 6)} · {fit.residuals.length} point(s)
                </div>
              )}
            </div>
          )}
          <Hint kind="warn">
            A calibration is only valid for the same capture settings (exposure, gain, aperture, display brightness) as the reference
            measurements.
          </Hint>
        </>
      )}
    </Section>
  );
}

function SpatialSection() {
  const sp = spatial.value;
  const line = selectedLine.value;
  const [known, setKnown] = useState<number | null>(null);
  const L = line ? Math.hypot(line.x1 - line.x0, line.y1 - line.y0) : 0;
  const ppm = frames.A.value?.meta.pixelsPerMm;
  return (
    <Section title="Spatial calibration" help="cal.spatial">
      <Field label="Unit" help="cal.spatialUnit">
        <Select<SpatialUnit>
          value={sp.unit}
          testId="spatial-unit"
          options={SPATIAL_UNITS.map((u) => ({ value: u.id, label: `${u.id} — ${u.label}` }))}
          onChange={(v) => updateSignal(spatial, { unit: v })}
        />
      </Field>
      {sp.unit !== 'px' && (
        <NumberField
          label="Pixel size"
          help="cal.perPixel"
          value={sp.perPixel}
          min={1e-12}
          testId="spatial-per-pixel"
          unit={`${sp.unit}/px`}
          onChange={(v) => updateSignal(spatial, { perPixel: v })}
        />
      )}
      {sp.unit !== 'px' && (
        <div class="muted small">
          = {fmt(1 / sp.perPixel, 6)} px/{sp.unit}
        </div>
      )}
      <div class="subhead">From a known distance</div>
      <div class="inline wrap">
        <Button small icon="calibrate" help="cal.tool" onClick={() => (tool.value = 'calibrate')}>
          Draw reference line
        </Button>
        {line && (
          <>
            <span class="muted small">
              or use {line.name} ({fmt(L, 5)} px):
            </span>
            <NumberInput value={known} placeholder="known length" onChange={setKnown} live min={0} />
            <span class="unit">{sp.unit === 'px' ? 'mm' : sp.unit}</span>
            <Button
              small
              kind="primary"
              disabled={!known || !L}
              help="cal.applyLine"
              onClick={() => {
                const unit = sp.unit === 'px' ? 'mm' : sp.unit;
                spatial.value = { unit, perPixel: known! / L };
              }}
            >
              Apply
            </Button>
          </>
        )}
      </div>
      {ppm && (
        <Button small help="cal.fromFile" onClick={() => (spatial.value = { unit: 'mm', perPixel: 1 / ppm })}>
          Use file resolution ({fmt(ppm, 5)} px/mm)
        </Button>
      )}
      {SPATIAL_UNITS.find((u) => u.id === sp.unit)?.angular && (
        <Hint>Angular units assume a constant angle per pixel (small field of view, no lens distortion).</Hint>
      )}
      <Button small kind="ghost" help="cal.resetSpatial" onClick={() => (spatial.value = { unit: 'px', perPixel: 1 })}>
        Reset to pixels
      </Button>
    </Section>
  );
}

export function CalibrationPanel() {
  return (
    <div class="calibration-panel">
      <PhotometricSection />
      <SpatialSection />
    </div>
  );
}

/** Modal shown after drawing a line with the calibrate tool. */
export function CalibrateDialog() {
  const req = calibrationRequest.value;
  const [len, setLen] = useState<number | null>(null);
  const [unit, setUnit] = useState<SpatialUnit>(spatial.value.unit === 'px' ? 'mm' : spatial.value.unit);
  if (!req) return null;
  const apply = () => {
    if (!len) return;
    spatial.value = { unit, perPixel: len / req.lengthPx };
    calibrationRequest.value = null;
    notify('success', `Spatial scale set: 1 px = ${fmt(len / req.lengthPx, 6)} ${unit}`);
  };
  return (
    <div class="modal-backdrop" onClick={() => (calibrationRequest.value = null)}>
      <div class="modal" onClick={(e) => e.stopPropagation()} data-testid="calibrate-dialog">
        <h3>Spatial calibration</h3>
        <p>
          Reference line length: <b class="mono">{fmt(req.lengthPx, 6)} px</b>
        </p>
        <div class="inline">
          <span>Known length</span>
          <NumberInput value={len} onChange={setLen} testId="calibrate-length" placeholder="e.g. 10" live min={0} />
          <Select<SpatialUnit>
            value={unit}
            options={SPATIAL_UNITS.filter((u) => u.id !== 'px').map((u) => ({ value: u.id, label: u.id }))}
            onChange={setUnit}
          />
        </div>
        <div class="modal-actions">
          <Button kind="ghost" onClick={() => (calibrationRequest.value = null)}>
            Cancel
          </Button>
          <Button kind="primary" disabled={!len} onClick={apply} testId="calibrate-apply">
            Apply
          </Button>
        </div>
      </div>
    </div>
  );
}
